import { formatDateTime } from "../../../lib/datetime/displayDateTime";
import {
  formatTimeWindowLabel,
} from "../../../lib/anomaly/timeGates";
import { useEffect, useState, useRef, type ReactNode } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

// @mui material components
import Paper from "@mui/material/Paper";
import Icon from "@mui/material/Icon";
import {
  Grid,
  Typography,
  Box,
  Chip,
  Divider,
  Avatar,
  Stack,
  TextField,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import Button from "@mui/material/Button";
import { alpha } from "@mui/material/styles";
import { PageListContainer } from "../../../components/Page";
import {
  resolvePriorityColor,
  geminiStatusColors,
} from "../../../assets/theme/geminiPalette";
import { useConfirm } from "../../../components/feedback";
import { incidentService } from "../../../services/incidentService";
import {
  IncidentDetailsResponse,
  IncidentPriority,
} from "../../../types/incident";
import IncidentSourcesSection from "./components/IncidentSourcesSection";
import OriginalEmailDialog from "./components/OriginalEmailDialog";
import type { OriginalEmailData } from "../../../lib/incident/originalEmailPreview";
import type { AnomalyEvent, DetectedAnomalyEvent } from "../../../types/anomalyEvent";
import { getData } from "../../../api/api";
import ProjectDialog from "../../../components/GenericDialog";
import IncidentModel from "../../incidents/model";
import {
  getIncidentPriorityLabel,
  INCIDENT_PRIORITY_OPTIONS,
  normalizeIncidentPriority,
  toIncidentPriorityEnum,
} from "../../incidents/lib/constants";
import {
  readIncidentsWorkspaceSelection,
} from "../../../lib/incident/workspaceSelection";
import { useIncidentCustomerName } from "../../../lib/incident/useIncidentCustomerName";
import {
  emailRecipientFallback,
  isEmailNotificationAvailable,
} from "../../../lib/incident/emailNotification";
import {
  isWhatsAppNotificationAvailable,
  whatsappRecipientFallback,
} from "../../../lib/incident/whatsappNotification";
import {
  pageButtonOutlinedPrimaryProps,
  pageButtonPrimaryProps,
  pageButtonSaveSuccessProps,
} from "../../../lib/ui/pageButtons";
import { IconAutocomplete } from "../../../components/forms";
import { useAnomalyGuideClassification } from "../../incidents/lib/useAnomalyGuideClassification";
import assignIncidentBody from "../../incidents/ui/components/AssignIncidentDialogBody";
import commentIncidentBody from "../../incidents/ui/components/CommentIncidentDialogBody";
import statusIncidentBody from "../../incidents/ui/components/StatusIncidentDialogBody";

const shortenId = (value?: string | null) => {
  if (!value) return "";
  if (value.length <= 12) return value;
  return `${value.slice(0, 5)}.....${value.slice(-4)}`;
};

const unlabeledValue = (value?: string | null) => {
  const text = value?.trim();
  return text ? text : "-";
};

const CopyableId = ({ value }: { value?: string | null }) => {
  const { t } = useTranslation();
  if (!value || String(value).trim().length === 0) {
    return <>-</>;
  }

  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
      <Tooltip title={value} arrow placement="top">
        <Typography component="span" sx={{ fontSize: "inherit" }}>
          {shortenId(value)}
        </Typography>
      </Tooltip>
      <Tooltip title={t("common.copyFullId")} arrow placement="top">
        <IconButton
          size="small"
          sx={{ p: 0.25 }}
          onClick={() => {
            navigator.clipboard?.writeText(value);
          }}
        >
          <Icon sx={{ fontSize: "0.75rem" }}>content_copy</Icon>
        </IconButton>
      </Tooltip>
    </Box>
  );
};

function SidebarField({
  label,
  children,
  action,
}: {
  label: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Box
      sx={{
        py: 1,
        px: 0,
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, mb: 0.35 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontWeight: 600,
            textTransform: "uppercase",
            fontSize: "0.65rem",
            letterSpacing: "0.06em",
          }}
        >
          {label}
        </Typography>
        {action}
      </Box>
      <Box sx={{ fontSize: "0.8125rem", color: "text.primary", minHeight: 22 }}>{children}</Box>
    </Box>
  );
}

function DetailClassificationEditor({
  incident,
  saving,
  onCancel,
  onUpdate,
}: {
  incident: IncidentDetailsResponse;
  saving: boolean;
  onCancel: () => void;
  onUpdate: (category: string, subcategory: string, description?: string) => void;
}) {
  const { t } = useTranslation();
  const {
    category,
    subcategory,
    setCategory,
    setSubcategory,
    categoryOptions,
    subcategoryOptions,
    classificationGuidance,
    taxonomyReady,
    taxonomyLoading,
  } = useAnomalyGuideClassification(
    { category: incident.category, subcategory: incident.subcategory },
    incident.id,
  );

  return (
    <>
      <SidebarField label={t("incidents.category")}>
        <IconAutocomplete
          options={categoryOptions}
          value={category}
          onChange={setCategory}
          placeholder={t("anomalies.selectCategory")}
          loading={taxonomyLoading}
          noOptionsText={
            taxonomyLoading
              ? t("anomalies.form.loadingSubcategories")
              : t("anomalies.form.noSubcategories")
          }
        />
      </SidebarField>
      <SidebarField label={t("incidents.subcategory")}>
        <IconAutocomplete
          options={subcategoryOptions}
          value={subcategory}
          onChange={setSubcategory}
          placeholder={
            category ? t("anomalies.form.searchSubcategory") : t("anomalies.selectCategoryFirst")
          }
          disabled={!category}
          loading={taxonomyLoading}
          noOptionsText={
            !category
              ? t("anomalies.selectCategoryFirstLong")
              : taxonomyLoading
                ? t("anomalies.form.loadingSubcategories")
                : t("anomalies.form.noSubcategories")
          }
        />
        <Stack direction="row" spacing={0.75} justifyContent="flex-end" sx={{ mt: 1 }}>
          <Button
            {...pageButtonOutlinedPrimaryProps}
            size="small"
            onClick={onCancel}
            disabled={saving}
          >
            {t("common.cancel")}
          </Button>
          <Button
            {...pageButtonPrimaryProps}
            size="small"
            onClick={() => {
              if (!category || !subcategory) return;
              const classificationChanged =
                category !== (incident.category ?? "").trim() ||
                subcategory !== (incident.subcategory ?? "").trim();
              onUpdate(
                category,
                subcategory,
                taxonomyReady && classificationChanged && classificationGuidance != null
                  ? classificationGuidance
                  : undefined,
              );
            }}
            disabled={saving || !category || !subcategory}
          >
            {t("common.update")}
          </Button>
        </Stack>
      </SidebarField>
    </>
  );
}

function toDisplayText(value: unknown): string {
  if (typeof value === "string") {
    return value.trim();
  }

  if (value == null) {
    return "";
  }

  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value).trim();
  }

  return "";
}

function isAnomalyEventObject(value: unknown): value is AnomalyEvent {
  if (!value || typeof value !== "object") {
    return false;
  }

  const obj = value as Record<string, unknown>;
  return (
    typeof obj.category === "string" ||
    typeof obj.subCategory === "string" ||
    typeof obj.sourceSystemType === "string" ||
    typeof obj.id === "string"
  );
}

function resolveAnomalyEventFromDetected(detected: DetectedAnomalyEvent): AnomalyEvent {
  if (isAnomalyEventObject(detected.definition)) {
    return detected.definition;
  }

  if (isAnomalyEventObject(detected)) {
    return detected as unknown as AnomalyEvent;
  }

  return {};
}

function formatAnomalyEventTitle(event: AnomalyEvent): string {
  const definition = toDisplayText(event.definition);
  if (definition) {
    return definition;
  }

  return event.id || "Anomaly event";
}

function formatAnomalyEventMeta(event: AnomalyEvent): string {
  const windowLabel = formatTimeWindowLabel(event.validFrom, event.validTo);
  const timeGates = [
    windowLabel ? i18n.t("incidents.timeGateHour", { window: windowLabel }) : null,
    event.outsideWorkingHours ? i18n.t("incidents.outsideHours") : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const parts = [
    event.sourceSystemType,
    event.activityPeriod,
    event.thresholdType && event.threshold != null
      ? `${event.thresholdType}: ${event.threshold}`
      : event.threshold != null
        ? `Threshold: ${event.threshold}`
        : null,
    timeGates || null,
  ].filter(Boolean);

  return parts.join(" · ");
}

function formatDetectedAnomalyStats(detected: DetectedAnomalyEvent): string {
  const parts = [
    detected.currentCount != null ? `Current: ${detected.currentCount}` : null,
    detected.baselineCount != null ? `Baseline: ${detected.baselineCount}` : null,
    detected.calculatedPercent != null
      ? `Deviation: ${Number(detected.calculatedPercent).toFixed(2)}%`
      : null,
    detected.detectedAt ? `Detected: ${formatDateTime(detected.detectedAt)}` : null,
  ].filter(Boolean);

  return parts.join(" · ");
}

function IncidentDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [incident, setIncident] = useState<IncidentDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState("UPDATE");
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState("");
  const [showTagInput, setShowTagInput] = useState(false);
  const [affectedUsers, setAffectedUsers] = useState<string[]>([]);
  const [newAffectedUser, setNewAffectedUser] = useState("");
  const [showAffectedInput, setShowAffectedInput] = useState(false);
  const [sourceIps, setSourceIps] = useState<string[]>([]);
  const [newSourceIp, setNewSourceIp] = useState("");
  const [showSourceIpInput, setShowSourceIpInput] = useState(false);
  const [destinationIps, setDestinationIps] = useState<string[]>([]);
  const [newDestinationIp, setNewDestinationIp] = useState("");
  const [showDestinationIpInput, setShowDestinationIpInput] = useState(false);
  const [loadingBody, setLoadingBody] = useState(false);
  const [originalEmailOpen, setOriginalEmailOpen] = useState(false);
  const [originalEmail, setOriginalEmail] = useState<{
    bodyContent: string;
    contentType?: string;
    emailData: OriginalEmailData;
  } | null>(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState<null | HTMLElement>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);
  const [sendingWhatsApp, setSendingWhatsApp] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [analysisEditing, setAnalysisEditing] = useState(false);
  const [analysisDraft, setAnalysisDraft] = useState("");
  const [savingAnalysis, setSavingAnalysis] = useState(false);
  const [classificationEditing, setClassificationEditing] = useState(false);
  const [savingClassification, setSavingClassification] = useState(false);
  const confirm = useConfirm();

  // Dialog refs
  const assignIncidentDialogRef = useRef<any>(null);
  const commentIncidentDialogRef = useRef<any>(null);
  const statusIncidentDialogRef = useRef<any>(null);

  useEffect(() => {
    const fetchIncident = async () => {
      if (!id) {
        setError("Incident ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await incidentService.getIncidentDetails(id);
        setIncident(response);
        setTags(response.tags || []);
        setAffectedUsers(response.affectedUsers || []);
        setSourceIps(response.sourceIps || []);
        setDestinationIps(response.destinationIps || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching incident:", err);
        setError(i18n.t("incidents.failedLoad"));
      } finally {
        setLoading(false);
      }
    };

    fetchIncident();
  }, [id]);

  useEffect(() => {
    setAnalysisEditing(false);
    setClassificationEditing(false);
  }, [incident?.id]);

  const handleBack = () => {
    if (!id) {
      navigate("/incidents");
      return;
    }

    const stored = readIncidentsWorkspaceSelection();
    navigate("/incidents", {
      state: {
        selectedIncidentId: id,
        selectedFolder: stored?.folder,
      },
    });
  };

  const handleViewBody = async () => {
    if (!incident?.openSearchId) {
      showNotification("Error", "Database ID not found", "error");
      return;
    }

    try {
      setLoadingBody(true);

      const data = await getData(`/opensearch/surelog-events/_doc/${incident.openSearchId}`, {
        headers: {
          accept: "*/*",
        },
        skipGlobalLoading: true,
      });

      if (data._source) {
        const bodyData = data._source.body;
        const emailData: OriginalEmailData = {
          subject: data._source.subject,
          from: data._source.from,
          toRecipients: data._source.toRecipients,
          ccRecipients: data._source.ccRecipients,
          bccRecipients: data._source.bccRecipients,
          sentDateTime: data._source.sentDateTime,
          receivedDateTime: data._source.receivedDateTime,
        };

        if (bodyData && bodyData.content) {
          setOriginalEmail({
            bodyContent: bodyData.content,
            contentType: bodyData.contentType,
            emailData,
          });
          setOriginalEmailOpen(true);
        } else {
          showNotification("Info", "No body content found in document", "info");
        }
      } else {
        showNotification("Info", "No document found for this Database ID", "info");
      }
    } catch (error) {
      console.error("Error fetching body content:", error);
      showNotification("Error", "Failed to fetch body content", "error");
    } finally {
      setLoadingBody(false);
    }
  };

  const formatComment = (comment: string) => {
    // First split the entire comment by "|"
    console.log(comment);
    const parts = comment.split("|");
    console.log(parts);
    // Then process each part
    const formattedParts = parts.map((part) => {
      // Split each part by ":"
      const colonSplit = part.split(":");
      if (colonSplit.length >= 2) {
        // First part (before ":") in bold, rest in span
        const boldPart = colonSplit[0].trim();
        const spanPart = colonSplit.slice(1).join(":").trim();
        return `<b>${boldPart} : </b><span>${spanPart}</span>`;
      }
      return part.trim();
    });

    // Join all parts with <br>
    return formattedParts.join("<br>");
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !incident) return;

    try {
      // Format the comment before sending
      const formattedComment = formatComment(newComment.trim());

      // Call the API to add a comment
      await incidentService.addComment(incident.id, {
        comment: formattedComment,
        type: commentType as "UPDATE" | "RESOLUTION" | "ESCALATION" | "SYSTEM",
      });

      // Refresh incident data to get the updated comments
      const response = await incidentService.getIncidentDetails(incident.id);
      setIncident(response);
      setNewComment("");

      showNotification("Success", "Comment added successfully!", "success");
    } catch (error) {
      console.error("Error adding comment:", error);
      showNotification("Error", "Failed to add comment", "error");
    }
  };

  const handleSendWhatsAppNotification = async () => {
    if (!incident) return;

    try {
      setSendingWhatsApp(true);
      const response = await incidentService.sendWhatsAppNotification(incident.id);
      const refreshed = await incidentService.getIncidentDetails(incident.id);
      setIncident(refreshed);
      showNotification(
        "Success",
        t("incidents.sendWhatsAppSuccess", {
          recipient: response.recipient || whatsappRecipientFallback(incident),
        }),
        "success"
      );
    } catch (error) {
      console.error("Error sending WhatsApp notification:", error);
      showNotification("Error", t("incidents.sendWhatsAppFailed"), "error");
    } finally {
      setSendingWhatsApp(false);
    }
  };

  const handleSendEmailNotification = async () => {
    if (!incident) return;

    try {
      setSendingEmail(true);
      const response = await incidentService.sendEmailNotification(incident.id);
      const refreshed = await incidentService.getIncidentDetails(incident.id);
      setIncident(refreshed);
      showNotification(
        "Success",
        t("incidents.sendEmailSuccess", {
          recipient: response.recipient || emailRecipientFallback(incident),
        }),
        "success"
      );
    } catch (error) {
      console.error("Error sending email notification:", error);
      showNotification("Error", t("incidents.sendEmailFailed"), "error");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!incident) return;

    try {
      await incidentService.assignToMe(incident.id);
      // Refresh incident data
      const response = await incidentService.getIncidentDetails(incident.id);
      setIncident(response);
      showNotification(
        "Success",
        "Incident assigned to you successfully!",
        "success"
      );
    } catch (error) {
      console.error("Error assigning incident:", error);
      showNotification("Error", "Failed to assign incident", "error");
    }
  };

  const handleAssignToGroup = () => {
    if (!incident) return;
    if (assignIncidentDialogRef.current) {
      assignIncidentDialogRef.current.showDialog(
        IncidentModel.assignDialogOpts(t),
        incident
      );
    }
  };

  const handleStatusChange = () => {
    if (!incident) return;
    if (statusIncidentDialogRef.current) {
      statusIncidentDialogRef.current.showDialog(
        IncidentModel.statusDialogOpts(t),
        incident
      );
    }
  };

  const handleAddTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
      setShowTagInput(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const buildUpdateRequest = (
    priority: IncidentPriority,
    patch?: { category?: string; subcategory?: string; description?: string },
  ) => {
    if (!incident) return null;
    return {
      id: incident.id,
      title: incident.title,
      description: patch?.description ?? incident.description ?? "",
      priority: toIncidentPriorityEnum(priority),
      sourceSystemId: incident.sourceSystem?.id ?? "",
      assignedTo: incident.assignedTo || undefined,
      assignedGroup: incident.assignedGroup || undefined,
      category: patch?.category ?? incident.category ?? "",
      subcategory: patch?.subcategory ?? incident.subcategory ?? "",
      tags: tags,
      affectedUsers: affectedUsers.length ? affectedUsers : undefined,
      sourceIps: sourceIps.length ? sourceIps : undefined,
      destinationIps: destinationIps.length ? destinationIps : undefined,
      metadata: (incident.metadata as Record<string, any>) || {},
      openSearchId: incident.openSearchId || "",
    };
  };

  const refreshIncident = async () => {
    if (!incident) return;
    const response = await incidentService.getIncidentDetails(incident.id);
    setIncident(response);
  };

  const handleSaveChanges = async () => {
    if (!incident) return;

    try {
      const payload = buildUpdateRequest(incident.priority);
      if (!payload) return;
      await incidentService.updateIncident(payload);
      await refreshIncident();

      showNotification("Success", "Incident updated successfully!", "success");
    } catch (error) {
      console.error("Error saving changes:", error);
      showNotification("Error", "Failed to save changes", "error");
    }
  };

  const handleSaveAnalysis = async () => {
    if (!incident || savingAnalysis) return;
    setSavingAnalysis(true);
    try {
      const payload = buildUpdateRequest(incident.priority, { description: analysisDraft });
      if (!payload) return;
      await incidentService.updateIncident(payload);
      await refreshIncident();
      setAnalysisEditing(false);
      showNotification("Success", t("incidents.updatedSuccess"), "success");
    } catch (error) {
      console.error("Error updating analysis content:", error);
      showNotification("Error", t("incidents.updateFailed"), "error");
    } finally {
      setSavingAnalysis(false);
    }
  };

  const handleSaveClassification = async (
    category: string,
    subcategory: string,
    description?: string,
  ) => {
    if (!incident || savingClassification) return;
    setSavingClassification(true);
    try {
      const payload = buildUpdateRequest(incident.priority, {
        category,
        subcategory,
        ...(description !== undefined ? { description } : {}),
      });
      if (!payload) return;
      await incidentService.updateIncident(payload);
      await refreshIncident();
      if (description !== undefined) {
        setAnalysisDraft(description);
        setAnalysisEditing(false);
      }
      setClassificationEditing(false);
      showNotification("Success", t("incidents.updatedSuccess"), "success");
    } catch (error) {
      console.error("Error updating incident classification:", error);
      showNotification("Error", t("incidents.updateFailed"), "error");
    } finally {
      setSavingClassification(false);
    }
  };

  // Submit action handlers for dialogs
  const assignIncidentSubmitAction = async (formJson: any) => {
    const incidentId = formJson.incidentId;

    try {
      await confirm({
        title: t("common.confirmAssignment"),
        description:
          formJson.assignmentType === "group"
            ? "Assign this incident to the selected group?"
            : "Assign this incident to the selected user?",
        confirmationText: "Confirm",
        cancellationText: t("common.cancel"),
      });
    } catch {
      return false;
    }

    if (formJson.assignmentType === "user") {
      try {
        await incidentService.assignToMe(incidentId);
        showNotification(
          "Success",
          "Incident assigned successfully!",
          "success"
        );
        // Refresh incident data
        if (incident) {
          const response = await incidentService.getIncidentDetails(incident.id);
          setIncident(response);
        }
      } catch (e) {
        console.error(e);
        showNotification("Error", "Failed to assign incident", "error");
        return false;
      }
    } else if (formJson.assignmentType === "group") {
      try {
        await incidentService.assignToGroup(
          incidentId,
          { assignedGroup: formJson.assignedGroup },
          formJson.assignedGroup
        );
        showNotification(
          "Success",
          "Incident assigned to group successfully!",
          "success"
        );
        // Refresh incident data
        if (incident) {
          const response = await incidentService.getIncidentDetails(incident.id);
          setIncident(response);
        }
      } catch (e) {
        console.error(e);
        showNotification(
          "Error",
          "Failed to assign incident to group",
          "error"
        );
        return false;
      }
    }

    return true;
  };

  const commentIncidentSubmitAction = (formJson: any) => {
    incidentService
      .addComment(formJson.incidentId, {
        comment: formJson.comment,
        type: formJson.type,
      })
      .then(() => {
        showNotification("Success", "Comment added successfully!", "success");
        // Refresh incident data
        if (incident) {
          incidentService.getIncidentDetails(incident.id).then((response) => {
            setIncident(response);
          });
        }
      })
      .catch((e) => {
        console.error(e);
        showNotification("Error", "Failed to add comment", "error");
      });
  };

  const statusIncidentSubmitAction = async (formJson: any) => {
    try {
      await confirm({
        title: t("common.confirmStatusChange"),
        description: `Change incident status to ${formJson.status}?`,
        confirmationText: "Confirm",
        cancellationText: t("common.cancel"),
      });
    } catch {
      return false;
    }

    try {
      await incidentService.updateStatus(
        formJson.incidentId,
        {
          status: formJson.status,
          resolution: formJson.resolution || undefined,
        },
        formJson.status
      );
      showNotification("Success", "Status updated successfully!", "success");
      // Refresh incident data
      if (incident) {
        const response = await incidentService.getIncidentDetails(incident.id);
        setIncident(response);
      }
      return true;
    } catch (e) {
      console.error(e);
      showNotification("Error", "Failed to update status", "error");
      return false;
    }
  };

  const showNotification = (
    title: string,
    message: string,
    type: "success" | "error" | "info" | "warning"
  ) => {
    // Use global notification system
    window.dispatchEvent(
      new CustomEvent("showGlobalNotification", {
        detail: {
          title,
          message,
          type,
          timestamp: Date.now(),
        },
      })
    );
  };

  const getPriorityColor = (priority: string) => resolvePriorityColor(priority);

  const priorityOptions = INCIDENT_PRIORITY_OPTIONS.map((option) => option.value);

  const handlePriorityClick = (event: React.MouseEvent<HTMLElement>) => {
    setPriorityMenuAnchor(event.currentTarget);
  };

  const handlePriorityMenuClose = () => {
    setPriorityMenuAnchor(null);
    setSelectedPriority(null);
  };

  const handleActionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setActionMenuAnchor(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
  };

  const confirmAction = (
    title: string,
    description: string,
    action: () => void | Promise<void>
  ) => {
    confirm({
      title,
      description,
      confirmationText: "Confirm",
      cancellationText: "Cancel",
    })
      .then(() => action())
      .catch(() => {
        // User cancelled
      });
  };

  const handlePrioritySelect = (newPriority: IncidentPriority) => {
    const currentPriority = normalizeIncidentPriority(incident?.priority);
    if (incident && newPriority !== currentPriority) {
      setSelectedPriority(newPriority);
      handlePriorityMenuClose();
      
      confirm({
        title: 'Change Priority',
        description: `Are you sure you want to change the priority from ${getIncidentPriorityLabel(incident.priority)} to ${getIncidentPriorityLabel(newPriority)}?`,
        confirmationText: 'Confirm',
        cancellationText: t("common.cancel"),
      })
        .then(() => {
          updateIncidentPriority(newPriority);
        })
        .catch(() => {
          // User cancelled
        });
    } else {
      handlePriorityMenuClose();
    }
  };

  const updateIncidentPriority = async (newPriority: IncidentPriority) => {
    if (!incident) return;

    try {
      const payload = buildUpdateRequest(newPriority);
      if (!payload) return;
      await incidentService.updateIncident(payload);

      // Refresh incident data
      const updatedIncident = await incidentService.getIncidentDetails(incident.id);
      setIncident(updatedIncident);
      
      // Show success notification
      window.dispatchEvent(new CustomEvent('showGlobalNotification', {
        detail: {
          color: 'success',
          icon: 'check_circle',
          title: 'Priority Updated',
          content: `Priority has been changed to ${getIncidentPriorityLabel(newPriority)}`,
        }
      }));
    } catch (error) {
      console.error('Error updating priority:', error);
      window.dispatchEvent(new CustomEvent('showGlobalNotification', {
        detail: {
          color: 'error',
          icon: 'error',
          title: 'Update Failed',
          content: 'Failed to update incident priority. Please try again.',
        }
      }));
    }
  };

  const getStatusColor = (status: string) => {
    const key = status?.toUpperCase() as keyof typeof geminiStatusColors;
    return geminiStatusColors[key] ?? geminiStatusColors.default;
  };

  const incidentCustomerName = useIncidentCustomerName(incident);
  const sourceSystemLabel = [
    incident?.sourceSystem?.name?.trim(),
    incident?.sourceSystem?.id?.trim(),
  ]
    .filter(Boolean)
    .join(" ");
  const incidentTitleText = incident?.title?.trim() || t("incidents.untitled");
  const incidentDetectedAtText = incident?.detectedAt
    ? formatDateTime(incident.detectedAt, "medium")
    : "-";
  const canSendWhatsApp = isWhatsAppNotificationAvailable(incident);
  const canSendEmail = isEmailNotificationAvailable(incident);

  if (loading) {
    return (
      <PageListContainer>
        <Box p={3} textAlign="center">
          <Typography>{t("incidents.loadingDetails")}</Typography>
        </Box>
      </PageListContainer>
    );
  }

  if (error || !incident) {
    return (
      <PageListContainer>
        <Box p={3} textAlign="center">
          <Typography color="error">{error || t("incidents.notFound")}</Typography>
          <Button
            {...pageButtonPrimaryProps}
            onClick={handleBack}
            sx={{ mt: 2 }}
          >
            {t("incidents.backToIncidents")}
          </Button>
        </Box>
      </PageListContainer>
    );
  }

  return (
    <PageListContainer>
      <Stack
        spacing={0}
        sx={{ width: 1, minWidth: 0, height: "100%", minHeight: 0, overflow: "hidden" }}
      >
        {/* Dialog Components */}
        <ProjectDialog
          ref={assignIncidentDialogRef}
          submitAction={assignIncidentSubmitAction}
          body={assignIncidentBody}
        />
        <ProjectDialog
          ref={commentIncidentDialogRef}
          submitAction={commentIncidentSubmitAction}
          body={commentIncidentBody}
        />
        <ProjectDialog
          ref={statusIncidentDialogRef}
          submitAction={statusIncidentSubmitAction}
          body={statusIncidentBody}
        />
        
        <OriginalEmailDialog
          open={originalEmailOpen}
          onClose={() => setOriginalEmailOpen(false)}
          bodyContent={originalEmail?.bodyContent ?? ""}
          contentType={originalEmail?.contentType}
          emailData={originalEmail?.emailData}
        />
        <Paper
          elevation={0}
          sx={{
            position: "relative",
            zIndex: 2,
            flexShrink: 0,
            borderRadius: 0,
            borderBottom: 1,
            borderColor: "divider",
            px: { xs: 1.5, md: 2 },
            py: { xs: 1.25, md: 1 },
            mb: 0,
            bgcolor: "background.paper",
          }}
        >
          <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              onClick={handleBack}
              aria-label={t("incidents.backToIncidents")}
              sx={{ width: { xs: 44, md: 36 }, height: { xs: 44, md: 36 }, flexShrink: 0 }}
            >
              <Icon>arrow_back</Icon>
            </IconButton>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                component="button"
                type="button"
                variant="caption"
                onClick={handleBack}
                sx={{
                  appearance: "none",
                  WebkitAppearance: "none",
                  border: 0,
                  backgroundColor: "transparent",
                  background: "none",
                  boxShadow: "none",
                  cursor: "pointer",
                  p: 0,
                  m: 0,
                  font: "inherit",
                  fontWeight: 600,
                  color: "text.secondary",
                  "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                }}
              >
                Incidents
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ userSelect: "none" }}>
                /
              </Typography>
              <Box sx={{ minWidth: 0, overflow: "hidden", "& .MuiTypography-root": { fontSize: "0.75rem" } }}>
                <CopyableId value={incident.id} />
              </Box>
            </Stack>
            <Stack
              direction="row"
              spacing={1}
              sx={{ display: { xs: "none", md: "flex" }, flexShrink: 0 }}
            >
            <Button
              {...pageButtonSaveSuccessProps}
              size="small"
              startIcon={<Icon fontSize="small">save</Icon>}
              onClick={() =>
                confirmAction(
                  t("incidents.saveChanges"),
                  t("incidents.saveChangesConfirm"),
                  handleSaveChanges
                )
              }
            >
              {t("incidents.saveChanges")}
            </Button>
            <Button
              {...pageButtonPrimaryProps}
              size="small"
              startIcon={<Icon fontSize="small">bolt</Icon>}
              endIcon={<Icon fontSize="small">expand_more</Icon>}
              onClick={handleActionMenuOpen}
            >
              {t("incidents.actions")}
            </Button>
            </Stack>
          </Stack>
          {(incidentCustomerName || incident.fromMail?.trim()) ? (
            <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
              {incidentCustomerName ? (
                <Chip
                  size="small"
                  icon={<Icon sx={{ fontSize: "0.95rem !important" }}>business</Icon>}
                  label={incidentCustomerName}
                  color="primary"
                  variant="outlined"
                  sx={{ height: 32, fontWeight: 700, maxWidth: "100%" }}
                />
              ) : null}
              {incident.fromMail?.trim() ? (
                <Chip
                  size="small"
                  icon={<Icon sx={{ fontSize: "0.95rem !important" }}>email</Icon>}
                  label={incident.fromMail.trim()}
                  variant="outlined"
                  sx={{ height: 32, fontWeight: 600, maxWidth: "100%" }}
                />
              ) : null}
            </Stack>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ display: { xs: "flex", md: "none" } }}>
            <Button
              {...pageButtonSaveSuccessProps}
              startIcon={<Icon fontSize="small">save</Icon>}
              onClick={() =>
                confirmAction(
                  t("incidents.saveChanges"),
                  t("incidents.saveChangesConfirm"),
                  handleSaveChanges
                )
              }
              sx={{ flex: 1, minHeight: 44 }}
            >
              {t("incidents.saveChanges")}
            </Button>
            <Button
              {...pageButtonPrimaryProps}
              startIcon={<Icon fontSize="small">bolt</Icon>}
              endIcon={<Icon fontSize="small">expand_more</Icon>}
              onClick={handleActionMenuOpen}
              sx={{ flex: 1, minHeight: 44 }}
            >
              {t("incidents.actions")}
            </Button>
          </Stack>
          </Stack>
            <Menu
              anchorEl={actionMenuAnchor}
              open={Boolean(actionMenuAnchor)}
              onClose={handleActionMenuClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              sx={{ "& .MuiMenuItem-root": { minHeight: 48 } }}
            >
              <MenuItem
                onClick={() => {
                  confirmAction(
                    t("incidents.assignToMe"),
                    t("incidents.assignToMeConfirm"),
                    handleAssignToMe
                  );
                  handleActionMenuClose();
                }}
              >
                <ListItemIcon>
                  <Icon fontSize="small">person_add</Icon>
                </ListItemIcon>
                <ListItemText primary={t("incidents.assignToMe")} />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleAssignToGroup();
                  handleActionMenuClose();
                }}
              >
                <ListItemIcon>
                  <Icon fontSize="small">group_add</Icon>
                </ListItemIcon>
                <ListItemText primary={t("incidents.assignToGroup")} />
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleStatusChange();
                  handleActionMenuClose();
                }}
              >
                <ListItemIcon>
                  <Icon fontSize="small">swap_horiz</Icon>
                </ListItemIcon>
                <ListItemText primary={t("incidents.changeStatus")} />
              </MenuItem>
              <Divider />
              <MenuItem
                disabled={!canSendEmail || sendingEmail}
                onClick={() => {
                  confirmAction(
                    t("incidents.sendEmailConfirmTitle"),
                    t("incidents.sendEmailConfirm"),
                    handleSendEmailNotification
                  );
                  handleActionMenuClose();
                }}
              >
                <ListItemIcon>
                  <Icon fontSize="small">email</Icon>
                </ListItemIcon>
                <ListItemText
                  primary={t("incidents.sendEmail")}
                  secondary={!canSendEmail ? t("incidents.sendEmailUnavailable") : undefined}
                />
              </MenuItem>
              <MenuItem
                disabled={!canSendWhatsApp || sendingWhatsApp}
                onClick={() => {
                  confirmAction(
                    t("incidents.sendWhatsAppConfirmTitle"),
                    t("incidents.sendWhatsAppConfirm"),
                    handleSendWhatsAppNotification
                  );
                  handleActionMenuClose();
                }}
              >
                <ListItemIcon>
                  <Icon fontSize="small">chat</Icon>
                </ListItemIcon>
                <ListItemText
                  primary={t("incidents.sendWhatsApp")}
                  secondary={!canSendWhatsApp ? t("incidents.sendWhatsAppUnavailable") : undefined}
                />
              </MenuItem>
            </Menu>
        </Paper>

        <Box
          sx={{
            width: 1,
            minWidth: 0,
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            px: { xs: 1.5, sm: 2 },
            py: 2,
            pb: { xs: 10, md: 2 },
            bgcolor: (t) =>
              t.palette.mode === "light" ? alpha(t.palette.grey[500], 0.06) : "transparent",
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "flex-start",
            gap: { xs: 2, md: 2.5 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0, width: { xs: 1, md: "auto" } }}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 1,
              mb: 1.5,
              overflow: "hidden",
              bgcolor: "background.paper",
              borderColor: "divider",
            }}
          >
            <Box sx={{ px: 1.5, pt: 1.5, pb: 1.25 }}>
              {sourceSystemLabel ? (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{ fontWeight: 800, lineHeight: 1.4, mb: 0.25 }}
                >
                  {sourceSystemLabel}
                </Typography>
              ) : null}
              <Typography
                component="h1"
                variant="body2"
                sx={{
                  fontWeight: 400,
                  fontSize: (theme) => `calc(${theme.typography.body2.fontSize} + 2pt)`,
                  lineHeight: 1.45,
                  mb: 1.25,
                  color: "text.primary",
                }}
              >
                {incidentTitleText}
                <Box component="span" sx={{ color: "text.secondary" }}>
                  {" | "}
                  {incidentDetectedAtText}
                </Box>
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 0.75 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  letterSpacing="0.08em"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase" }}
                >
                  {t("incidents.analysisContent")}
                </Typography>
                {analysisEditing ? (
                  <Stack direction="row" spacing={0.75}>
                    <Button
                      {...pageButtonOutlinedPrimaryProps}
                      size="small"
                      onClick={() => setAnalysisEditing(false)}
                      disabled={savingAnalysis}
                    >
                      {t("common.cancel")}
                    </Button>
                    <Button
                      {...pageButtonPrimaryProps}
                      size="small"
                      onClick={() => void handleSaveAnalysis()}
                      disabled={savingAnalysis}
                    >
                      {t("common.update")}
                    </Button>
                  </Stack>
                ) : (
                  <IconButton
                    size="small"
                    aria-label={t("incidents.edit")}
                    onClick={() => {
                      setAnalysisDraft(incident.description ?? "");
                      setAnalysisEditing(true);
                    }}
                  >
                    <Icon fontSize="small">edit</Icon>
                  </IconButton>
                )}
              </Box>
              {analysisEditing ? (
                <TextField
                  fullWidth
                  multiline
                  minRows={4}
                  size="small"
                  value={analysisDraft}
                  onChange={(event) => setAnalysisDraft(event.target.value)}
                  placeholder={t("incidents.analysisContent")}
                />
              ) : (
                <Typography variant="body2" sx={{ lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                  {incident.description?.trim() || "—"}
                </Typography>
              )}
              <Box sx={{ mt: 1.25 }}>
                {incident?.openSearchId ? (
                  <Button
                    variant="text"
                    color="primary"
                    size="small"
                    onClick={handleViewBody}
                    disabled={loadingBody}
                    startIcon={<Icon fontSize="small">visibility</Icon>}
                    sx={{ px: 0 }}
                  >
                    {loadingBody ? t("common.loading") : t("incidents.viewOriginalEmail")}
                  </Button>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    {t("incidents.noDatabaseId")}
                  </Typography>
                )}
              </Box>
            </Box>
          </Paper>

          <IncidentSourcesSection matchedSources={incident.matchedSources} />

          {(incident.detectedAnomalyEvents?.length || incident.detectedAnomalies?.length) ? (
            <Paper
              variant="outlined"
              sx={{
                borderRadius: 1,
                mb: 1.5,
                overflow: "hidden",
                bgcolor: "background.paper",
                borderColor: "divider",
              }}
            >
              <Box
                sx={{
                  px: 1.25,
                  py: 0.65,
                  borderBottom: 1,
                  borderColor: "divider",
                  bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
                }}
              >
                <Typography variant="caption" fontWeight={700} letterSpacing="0.08em" color="text.secondary">
                  Detected anomaly events
                </Typography>
              </Box>
              <Box sx={{ px: 1.25, py: 1 }}>
                <Stack spacing={1}>
                  {(incident.detectedAnomalyEvents ?? []).map((detected, index) => {
                    const rule = resolveAnomalyEventFromDetected(detected);
                    const anomalyEventId = detected.anomalyEventId || rule.id;
                    const definitionText = toDisplayText(rule.definition);
                    const detectionStats = formatDetectedAnomalyStats(detected);
                    const ruleMeta = formatAnomalyEventMeta(rule);

                    return (
                    <Box
                      key={anomalyEventId || `${rule.category}-${rule.subCategory}-${index}`}
                      sx={{
                        pl: 1.25,
                        py: 0.5,
                        borderLeft: 3,
                        borderColor: "warning.main",
                        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
                        borderRadius: 0.5,
                      }}
                    >
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8125rem" }}>
                        {formatAnomalyEventTitle(rule)}
                      </Typography>
                      {definitionText && definitionText !== formatAnomalyEventTitle(rule) && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                          sx={{ whiteSpace: "pre-wrap", mt: 0.25 }}
                        >
                          {definitionText}
                        </Typography>
                      )}
                      {ruleMeta && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {ruleMeta}
                        </Typography>
                      )}
                      {detectionStats && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          {detectionStats}
                        </Typography>
                      )}
                      {anomalyEventId && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          Anomaly event ID: <CopyableId value={anomalyEventId} />
                        </Typography>
                      )}
                    </Box>
                    );
                  })}
                  {!incident.detectedAnomalyEvents?.length &&
                    incident.detectedAnomalies?.map((a) => (
                      <Box
                        key={a.id}
                        sx={{
                          pl: 1.25,
                          py: 0.5,
                          borderLeft: 3,
                          borderColor: "warning.main",
                          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
                          borderRadius: 0.5,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: "0.8125rem" }}>
                          {toDisplayText(a.definition) || a.activityType || a.id}
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              </Box>
            </Paper>
          ) : null}

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 1,
              mb: 1.5,
              overflow: "hidden",
              bgcolor: "background.paper",
              borderColor: "divider",
            }}
          >
            <Box
              sx={{
                px: 1.25,
                py: 0.65,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
              }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                letterSpacing="0.08em"
                color="text.secondary"
              >
                {t("incidents.details")}
              </Typography>
            </Box>
            <Box sx={{ px: 1.5, py: 1.25 }}>
                <Grid container spacing={1.5} sx={{ mt: 0 }}>
                  {/* Tags */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Tags
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 1,
                        alignItems: "center",
                      }}
                    >
                      {tags.length > 0 ? (
                        <>
                          {tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              variant="outlined"
                              color="primary"
                              size="small"
                              onDelete={() => handleRemoveTag(tag)}
                            />
                          ))}
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setShowTagInput(true)}
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }}
                          >
                            <Icon fontSize="small">add</Icon>
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            No tags assigned
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setShowTagInput(true)}
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }}
                          >
                            <Icon fontSize="small">add</Icon>
                          </IconButton>
                        </>
                      )}
                    </Box>
                    {showTagInput && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          maxWidth: 400,
                          mb: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder={t("common.addTag")}
                          value={newTag}
                          onChange={(e) => setNewTag(e.target.value)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleAddTag()
                          }
                          sx={{ flexGrow: 1 }}
                          autoFocus
                        />
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={handleAddTag}
                          disabled={!newTag.trim()}
                        >
                          Add
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowTagInput(false);
                            setNewTag("");
                          }}
                          sx={{ color: "text.secondary" }}
                        >
                          <Icon fontSize="small">close</Icon>
                        </IconButton>
                      </Box>
                    )}
                  </Grid>

                  {/* Affected Users */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Affected Users
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 1,
                        alignItems: "center",
                      }}
                    >
                      {affectedUsers.length > 0 ? (
                        <>
                          {affectedUsers.map((u, idx) => (
                            <Chip
                              key={`${u}-${idx}`}
                              label={u}
                              onDelete={() =>
                                setAffectedUsers(
                                  affectedUsers.filter((x) => x !== u)
                                )
                              }
                              size="small"
                            />
                          ))}
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setShowAffectedInput(true)}
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }}
                          >
                            <Icon fontSize="small">add</Icon>
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            No affected users
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setShowAffectedInput(true)}
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }}
                          >
                            <Icon fontSize="small">add</Icon>
                          </IconButton>
                        </>
                      )}
                    </Box>
                    {showAffectedInput && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          maxWidth: 400,
                          mb: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder={t("common.addAffectedUser")}
                          value={newAffectedUser}
                          onChange={(e) => setNewAffectedUser(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter" && newAffectedUser.trim()) {
                              setAffectedUsers([
                                ...affectedUsers,
                                newAffectedUser.trim(),
                              ]);
                              setNewAffectedUser("");
                              setShowAffectedInput(false);
                            }
                          }}
                          sx={{ flexGrow: 1 }}
                          autoFocus
                        />
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => {
                            if (newAffectedUser.trim()) {
                              setAffectedUsers([
                                ...affectedUsers,
                                newAffectedUser.trim(),
                              ]);
                              setNewAffectedUser("");
                              setShowAffectedInput(false);
                            }
                          }}
                        >
                          Add
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowAffectedInput(false);
                            setNewAffectedUser("");
                          }}
                          sx={{ color: "text.secondary" }}
                        >
                          <Icon fontSize="small">close</Icon>
                        </IconButton>
                      </Box>
                    )}
                  </Grid>

                  {/* Source IPs */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Source IPs
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 1,
                        alignItems: "center",
                      }}
                    >
                      {sourceIps.length > 0 ? (
                        <>
                          {sourceIps.map((ip, idx) => (
                            <Chip
                              key={`${ip}-${idx}`}
                              label={ip}
                              onDelete={() =>
                                setSourceIps(sourceIps.filter((x) => x !== ip))
                              }
                              size="small"
                            />
                          ))}
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setShowSourceIpInput(true)}
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }}
                          >
                            <Icon fontSize="small">add</Icon>
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            No source IPs
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setShowSourceIpInput(true)}
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }}
                          >
                            <Icon fontSize="small">add</Icon>
                          </IconButton>
                        </>
                      )}
                    </Box>
                    {showSourceIpInput && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          maxWidth: 400,
                          mb: 1,
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder={t("common.addSourceIp")}
                          value={newSourceIp}
                          onChange={(e) => setNewSourceIp(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter" && newSourceIp.trim()) {
                              setSourceIps([...sourceIps, newSourceIp.trim()]);
                              setNewSourceIp("");
                              setShowSourceIpInput(false);
                            }
                          }}
                          sx={{ flexGrow: 1 }}
                          autoFocus
                        />
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => {
                            if (newSourceIp.trim()) {
                              setSourceIps([...sourceIps, newSourceIp.trim()]);
                              setNewSourceIp("");
                              setShowSourceIpInput(false);
                            }
                          }}
                        >
                          Add
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowSourceIpInput(false);
                            setNewSourceIp("");
                          }}
                          sx={{ color: "text.secondary" }}
                        >
                          <Icon fontSize="small">close</Icon>
                        </IconButton>
                      </Box>
                    )}
                  </Grid>

                  {/* Destination IPs */}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: "bold", mb: 1 }}
                    >
                      Destination IPs
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 0.5,
                        mb: 1,
                        alignItems: "center",
                      }}
                    >
                      {destinationIps.length > 0 ? (
                        <>
                          {destinationIps.map((ip, idx) => (
                            <Chip
                              key={`${ip}-${idx}`}
                              label={ip}
                              onDelete={() =>
                                setDestinationIps(
                                  destinationIps.filter((x) => x !== ip)
                                )
                              }
                              size="small"
                            />
                          ))}
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => setShowDestinationIpInput(true)}
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }}
                          >
                            <Icon fontSize="small">add</Icon>
                          </IconButton>
                        </>
                      ) : (
                        <>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mr: 1 }}
                          >
                            No destination IPs
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={() => setShowDestinationIpInput(true)}
                            sx={{
                              width: 24,
                              height: 24,
                              backgroundColor: "primary.main",
                              color: "white",
                              "&:hover": { backgroundColor: "primary.dark" },
                            }}
                          >
                            <Icon fontSize="small">add</Icon>
                          </IconButton>
                        </>
                      )}
                    </Box>
                    {showDestinationIpInput && (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                          maxWidth: 400,
                        }}
                      >
                        <TextField
                          size="small"
                          placeholder={t("common.addDestinationIp")}
                          value={newDestinationIp}
                          onChange={(e) => setNewDestinationIp(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter" && newDestinationIp.trim()) {
                              setDestinationIps([
                                ...destinationIps,
                                newDestinationIp.trim(),
                              ]);
                              setNewDestinationIp("");
                              setShowDestinationIpInput(false);
                            }
                          }}
                          sx={{ flexGrow: 1 }}
                          autoFocus
                        />
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => {
                            if (newDestinationIp.trim()) {
                              setDestinationIps([
                                ...destinationIps,
                                newDestinationIp.trim(),
                              ]);
                              setNewDestinationIp("");
                              setShowDestinationIpInput(false);
                            }
                          }}
                        >
                          Add
                        </Button>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setShowDestinationIpInput(false);
                            setNewDestinationIp("");
                          }}
                          sx={{ color: "text.secondary" }}
                        >
                          <Icon fontSize="small">close</Icon>
                        </IconButton>
                      </Box>
                    )}
                  </Grid>
                </Grid>
            </Box>
          </Paper>

          {/* Activity / comments (Jira-style) */}
          <Paper variant="outlined" sx={{ borderRadius: 1, mb: 1.5, overflow: "hidden", bgcolor: "background.paper", borderColor: "divider" }}>
            <Box
              sx={{
                px: 1.25,
                py: 0.65,
                borderBottom: 1,
                borderColor: "divider",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.05),
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Typography variant="caption" fontWeight={700} letterSpacing="0.08em" color="text.secondary">
                Activity
              </Typography>
              <Chip size="small" label={incident.comments?.length ?? 0} sx={{ height: 20, fontSize: "0.65rem" }} variant="outlined" />
            </Box>
            <Box sx={{ p: 1.25 }}>
              <Box
                sx={{
                  maxHeight: 280,
                  overflowY: "auto",
                  pl: 0.5,
                  mb: 1.5,
                  borderLeft: 2,
                  borderColor: "divider",
                }}
              >
                {(incident.comments?.length ?? 0) > 0 ? (
                  <Stack spacing={1}>
                    {(incident.comments ?? []).map((comment, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          gap: 1,
                          p: 1,
                          borderRadius: 1,
                          bgcolor: "background.paper",
                          "&:hover": {
                            backgroundColor: ({ palette }) => palette.action.hover,
                          },
                        }}
                      >
                        {/* Avatar */}
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: "0.8rem",
                            backgroundColor: "primary.main",
                            color: "white",
                          }}
                        >
                          {(comment.author || "?").charAt(0).toUpperCase()}
                        </Avatar>

                        {/* Comment Content */}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          {/* Header with author and timestamp */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mb: 0.5,
                            }}
                          >
                            <Typography
                              variant="subtitle2"
                              fontWeight="600"
                              sx={{
                                color: "text.primary",
                                fontSize: "0.875rem",
                              }}
                            >
                              {comment.author || "—"}
                              <br />
                              <Typography
                                variant="caption"
                                sx={{ color: "text.secondary" }}
                              >
                                {formatDateTime(comment.createdAt)}
                              </Typography>
                            </Typography>
                          </Box>

                          {/* Comment text */}
                          <Box
                            sx={{
                              color: "text.primary",
                              lineHeight: 1.45,
                              wordBreak: "break-word",
                              mt: 0.5,
                              p: 1,
                              borderRadius: 1,
                              border: 1,
                              borderColor: "divider",
                            }}
                          >
                            <Chip
                              label={comment.type}
                              size="small"
                              variant="outlined"
                              color="primary"
                              sx={{ height: 22, fontSize: "0.7rem", mb: 0.5 }}
                            />
                            <Divider sx={{ mb: 1 }} />
                            <div
                              dangerouslySetInnerHTML={{
                                __html: formatComment(comment.comment),
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Stack>
                ) : (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      No comments yet.
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Add New Comment Section */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Write your comment here..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  variant="outlined"
                  size="small"
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {["UPDATE", "RESOLUTION", "ESCALATION", "SYSTEM"].map(
                      (type) => (
                        <Chip
                          key={type}
                          label={type}
                          size="small"
                          variant={commentType === type ? "filled" : "outlined"}
                          color={commentType === type ? "primary" : "default"}
                          onClick={() => setCommentType(type)}
                          sx={{ cursor: "pointer" }}
                        />
                      )
                    )}
                  </Box>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddComment}
                    disabled={!newComment.trim()}
                    size="small"
                  >
                    Add Comment
                  </Button>
                </Box>
              </Box>
            </Box>
          </Paper>

          {incident.resolution && (
            <Paper variant="outlined" sx={{ borderRadius: 1, mb: 1.5, overflow: "hidden", borderColor: "divider" }}>
              <Box sx={{ px: 1.25, py: 0.65, borderBottom: 1, borderColor: "divider", bgcolor: (t) => alpha(t.palette.success.main, 0.08) }}>
                <Typography variant="caption" fontWeight={700} letterSpacing="0.08em" color="text.secondary">
                  Resolution
                </Typography>
              </Box>
              <Box sx={{ px: 1.5, py: 1.25 }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap", fontSize: "0.8125rem", lineHeight: 1.5 }}>
                  {incident.resolution}
                </Typography>
              </Box>
            </Paper>
          )}

          {incident.metadata && Object.keys(incident.metadata).length > 0 && (
            <Paper variant="outlined" sx={{ borderRadius: 1, mb: 1.5, overflow: "hidden", borderColor: "divider" }}>
              <Box sx={{ px: 1.25, py: 0.65, borderBottom: 1, borderColor: "divider", bgcolor: (t) => alpha(t.palette.primary.main, 0.05) }}>
                <Typography variant="caption" fontWeight={700} letterSpacing="0.08em" color="text.secondary">
                  Metadata
                </Typography>
              </Box>
              <Box sx={{ px: 1.5, py: 1.25 }}>
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    p: 1,
                    fontSize: "0.75rem",
                    borderRadius: 0.5,
                    bgcolor: "action.hover",
                    border: 1,
                    borderColor: "divider",
                    overflow: "auto",
                    maxHeight: 220,
                  }}
                >
                  {JSON.stringify(incident.metadata, null, 2)}
                </Box>
              </Box>
            </Paper>
          )}
            </Box>

            <Box
              sx={{
                width: { xs: 1, md: 300, lg: 320 },
                flexShrink: 0,
                minWidth: 0,
                alignSelf: { xs: "stretch", md: "flex-start" },
              }}
            >
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  p: 0,
                  bgcolor: "background.paper",
                  borderColor: "divider",
                  position: { md: "sticky" },
                  top: 52,
                  maxHeight: { md: "calc(100vh - 100px)" },
                  overflow: { xs: "visible", md: "auto" },
                }}
              >
                <Box sx={{ px: 1.25, py: 0.75, borderBottom: 1, borderColor: "divider", bgcolor: (t) => alpha(t.palette.primary.main, 0.05) }}>
                  <Typography variant="caption" fontWeight={700} letterSpacing="0.08em" color="text.secondary">
                    Issue details
                  </Typography>
                </Box>
                <Box sx={{ px: 1.25, py: 0.5 }}>
                  <SidebarField label={t("incidents.status")}>
                    <Chip
                      label={incident.status?.replace(/_/g, " ") ?? "—"}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        bgcolor: getStatusColor(incident.status),
                        color: "common.white",
                        "& .MuiChip-label": { color: "common.white", px: 1 },
                      }}
                    />
                  </SidebarField>
                  <SidebarField label={t("incidents.priority")}>
                    <Chip
                      label={getIncidentPriorityLabel(incident.priority)}
                      size="small"
                      onClick={handlePriorityClick}
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        bgcolor: getPriorityColor(incident.priority),
                        color: "common.white",
                        "& .MuiChip-label": { color: "common.white", px: 1 },
                      }}
                      icon={
                        <Icon sx={{ color: "common.white !important", fontSize: "1rem !important", ml: 0.5 }}>
                          arrow_drop_down
                        </Icon>
                      }
                    />
                    <Menu
                      anchorEl={priorityMenuAnchor}
                      open={Boolean(priorityMenuAnchor)}
                      onClose={handlePriorityMenuClose}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                      {priorityOptions.map((priority) => {
                        const currentPriority = normalizeIncidentPriority(incident.priority);
                        const priorityLabel = getIncidentPriorityLabel(priority);
                        return (
                        <MenuItem
                          key={priority}
                          onClick={() => handlePrioritySelect(priority)}
                          selected={priority === currentPriority}
                          sx={{
                            minWidth: 160,
                            "&.Mui-selected": {
                              bgcolor: getPriorityColor(priority),
                              color: "common.white",
                              "&:hover": { bgcolor: getPriorityColor(priority), opacity: 0.92 },
                              "& .MuiListItemIcon-root": { color: "common.white" },
                            },
                          }}
                        >
                          <ListItemIcon>
                            <Box
                              sx={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                bgcolor: getPriorityColor(priority),
                              }}
                            />
                          </ListItemIcon>
                          <ListItemText primary={priorityLabel} />
                          {priority === currentPriority && (
                            <Icon sx={{ ml: 1, fontSize: "1.1rem" }}>check</Icon>
                          )}
                        </MenuItem>
                        );
                      })}
                    </Menu>
                  </SidebarField>
                  {classificationEditing ? (
                    <DetailClassificationEditor
                      incident={incident}
                      saving={savingClassification}
                      onCancel={() => setClassificationEditing(false)}
                      onUpdate={(category, subcategory, description) =>
                        void handleSaveClassification(category, subcategory, description)
                      }
                    />
                  ) : (
                    <>
                      <SidebarField
                        label={t("incidents.category")}
                        action={
                          <IconButton
                            size="small"
                            aria-label={t("incidents.edit")}
                            onClick={() => setClassificationEditing(true)}
                            sx={{ p: 0.25 }}
                          >
                            <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                          </IconButton>
                        }
                      >
                        <Typography variant="body2">{incident.category?.trim() || "—"}</Typography>
                      </SidebarField>
                      <SidebarField
                        label={t("incidents.subcategory")}
                        action={
                          <IconButton
                            size="small"
                            aria-label={t("incidents.edit")}
                            onClick={() => setClassificationEditing(true)}
                            sx={{ p: 0.25 }}
                          >
                            <Icon sx={{ fontSize: "1rem" }}>edit</Icon>
                          </IconButton>
                        }
                      >
                        <Typography variant="body2">{incident.subcategory?.trim() || "—"}</Typography>
                      </SidebarField>
                    </>
                  )}
                  <Box
                    sx={{
                      py: 1,
                      borderBottom: 1,
                      borderColor: "divider",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                      <Icon sx={{ fontSize: "1.15rem" }}>engineering</Icon>
                      <Typography variant="body2" fontWeight={700}>
                        {unlabeledValue(incident.assignedTo)}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {unlabeledValue(incident.assignedGroup)}
                      </Typography>
                    </Stack>
                  </Box>
                  <SidebarField label={t("incidents.created")}>
                    <Typography variant="body2">
                      {incident.createdDate ? formatDateTime(incident.createdDate, "medium") : "—"}
                    </Typography>
                  </SidebarField>
                  <SidebarField label={t("incidents.createdBy")}>
                    <Typography variant="body2">
                      {incident.createdBy?.trim() || "—"}
                    </Typography>
                  </SidebarField>
                  <SidebarField label={t("incidents.updated")}>
                    <Typography variant="body2">
                      {incident.lastModifiedDate ? formatDateTime(incident.lastModifiedDate, "medium") : "—"}
                    </Typography>
                  </SidebarField>
                  <SidebarField label={t("incidents.resolved")}>
                    <Typography variant="body2">
                      {incident.resolvedAt ? formatDateTime(incident.resolvedAt, "medium") : "—"}
                    </Typography>
                  </SidebarField>
                  {incident.fromMail && (
                    <SidebarField label={t("incidents.from")}>
                      <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                        {incident.fromMail}
                      </Typography>
                    </SidebarField>
                  )}
                  {incident.openSearchId && (
                    <SidebarField label={t("incidents.database")}>
                      <CopyableId value={incident.openSearchId} />
                    </SidebarField>
                  )}
                </Box>
              </Paper>
            </Box>
        </Box>
      </Stack>
    </PageListContainer>
  );
}

export default IncidentDetail;
